import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  Stack,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { WarehouseData } from './warehouses.types';
import axios from 'axios';
import { API_URL } from '../../config/config';

const Warehouse: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [warehouse, setWarehouse] = useState<WarehouseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        if (!user) return null;
        const res = await fetch(`${API_URL}/warehouse/${user.id}/${id}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to fetch warehouse');
        }
        const data: any = await res.json();
        setWarehouse(data.data);
      } catch (err: any) {
        setLoading(true);
        setError(err.message || 'Unexpected error');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStore();
    }
  }, [id, user]);

  const goToInventory = (id: string) => {
    navigate('/inventory/' + id);
  };

  const goToUpdateWarehouse = (id: string) => {
    navigate(`/update-warehouse/${id}`);
  };

  const addInventory = () => {
    navigate('/create-inventory');
  };

  const deleteStore = async (id: string) => {
    const confirmed = window.confirm(
      'Do you really want to delete this warehouse?'
    );

    if (confirmed && user) {
      try {
        await axios.delete(`${API_URL}/warehouse/${user.id}/${id}`);
        // Optionally refresh the warehouse list or give user feedback
        navigate('/warehouses');
      } catch (error) {
        console.error('Error deleting warehouse:', error);
        alert('Something went wrong while deleting the warehouse.');
      }
    } else {
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        navigate
        {error}
      </Alert>
    );
  }

  if (!warehouse) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        No warehouse found.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 2 }}>
      <Card sx={{ boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {warehouse.name}
          </Typography>
          <Typography variant="body1">
            <strong>Address:</strong> {warehouse.id_BusinessLocation.address}
          </Typography>
          <Typography variant="body1">
            <strong>Phone:</strong> {warehouse.phone_number}
          </Typography>
          <Typography variant="body1">
            <strong>Email:</strong> {warehouse.email}
          </Typography>
          <Typography variant="body1">
            <strong>Status:</strong> {warehouse.status}
          </Typography>
          <Typography variant="body1">
            <strong>Capacity:</strong> {warehouse.capacity}
          </Typography>
          <Typography variant="body1">
            <strong>Region:</strong> {warehouse.region}, <strong>City:</strong>{' '}
            {warehouse.city}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            <strong>Opening Hours:</strong>
          </Typography>
          <ul>
            {(() => {
              const hours =
                typeof warehouse.opening_hours === 'string'
                  ? JSON.parse(warehouse.opening_hours)
                  : warehouse.opening_hours;

              return (
                <>
                  <li>Mon-Fri: {hours?.['mon-fri'] ?? 'N/A'}</li>
                  <li>Sat: {hours?.sat ?? 'N/A'}</li>
                  <li>Sun: {hours?.sun ?? 'N/A'}</li>
                </>
              );
            })()}
          </ul>
          {warehouse.image_url && (
            <Box sx={{ mt: 2 }}>
              <img
                src={warehouse.image_url}
                alt={warehouse.name}
                style={{ maxWidth: '100%', borderRadius: 8 }}
              />
            </Box>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={() => goToUpdateWarehouse(warehouse.id)}
            >
              Update
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => deleteStore(warehouse.id)}
            >
              Delete
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ p: 2 }}>
        {warehouse.WarehouseInventories &&
        warehouse.WarehouseInventories.length > 0 ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h4" gutterBottom>
                Inventories
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Button
                  onClick={() => addInventory()}
                  variant="contained"
                  sx={{
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    padding: 0,
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    textAlign: 'center',
                  }}
                >
                  +
                </Button>
              </Box>
            </div>

            {warehouse.WarehouseInventories.map(inv => (
              <Card
                onClick={() => goToInventory(inv.id)}
                key={inv.id}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  '&:hover': { boxShadow: 6 },
                  cursor: 'pointer',
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary">
                    {inv.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quantity: {inv.quantity}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h4" gutterBottom>
              No Inventories
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={() => addInventory()}
                variant="contained"
                sx={{
                  minWidth: 0,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  padding: 0,
                  fontSize: '1.5rem',
                  lineHeight: 1,
                  textAlign: 'center',
                }}
              >
                +
              </Button>
            </Box>
          </div>
        )}
      </Box>
    </Box>
  );
};

export default Warehouse;
